import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { useCountry } from "@/lib/CountryContext";
import { COUNTRIES, isCountryActive } from "@/lib/countries";

/** Compact country picker that lives in the top navbar (desktop + mobile). */
export default function CountrySelector({ compact = false }) {
  const { code, country, setCountry } = useCountry();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--line)] bg-white hover:border-orange-300 transition text-sm font-semibold"
        data-testid="country-selector-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="text-lg leading-none">{country.flag}</span>
        {!compact && <span className="hidden sm:inline">{country.name}</span>}
        <ChevronDown size={13} className={`transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-2 w-56 max-h-[60vh] overflow-y-auto bg-white border border-[var(--line)] rounded-2xl shadow-xl py-1.5 z-50"
          data-testid="country-selector-menu"
        >
          {Object.values(COUNTRIES).map((c) => {
            const active = isCountryActive(c.code);
            const selected = c.code === code;
            return (
              <li key={c.code}>
                <button
                  type="button"
                  disabled={!active}
                  onClick={() => { setCountry(c.code); setOpen(false); }}
                  className={`w-full text-left px-3 py-2 text-sm inline-flex items-center justify-between gap-2 transition
                    ${selected ? "bg-orange-50 font-bold" : ""}
                    ${active ? "hover:bg-orange-50/70 text-[var(--ink)]" : "text-[var(--ink-soft)] cursor-not-allowed opacity-70"}`}
                  data-testid={`country-option-${c.code}`}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-base leading-none">{c.flag}</span>
                    <span>{c.name}</span>
                  </span>
                  {!active && (
                    <span className="text-[10px] uppercase tracking-wider bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                      Bientot
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
